import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Award, Users, Clock, Shield } from "lucide-react";

export default function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    {
      icon: Award,
      title: "Ekspertise",
      description: "Over 10 års erfaring med salgsrenovering",
    },
    {
      icon: Users,
      title: "Professionelt team",
      description: "Håndværkere med speciale i salgsoptimering",
    },
    {
      icon: Clock,
      title: "Hurtig levering",
      description: "Effektive projekter inden for tidsrammen",
    },
    {
      icon: Shield,
      title: "Garanti",
      description: "Fuld garanti på alt udført arbejde",
    },
  ];

  return (
    <section 
      id="om-os" 
      ref={ref}
      className="py-24 md:py-32 bg-primary text-primary-foreground"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">
              Om Salgsrenovering
            </span>
            <h2 
              className="mt-4 text-4xl md:text-5xl font-bold tracking-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Vi forstår hvad
              <br />
              købere søger
            </h2>
            <p className="mt-6 text-lg text-primary-foreground/80 leading-relaxed">
              Hos Salgsrenovering.dk kombinerer vi dyb viden om boligmarkedet med 
              håndværksmæssig ekspertise. Vi ved præcis hvilke renoveringer der 
              tiltrækker købere og øger din boligs værdi mest effektivt.
            </p>
            <p className="mt-4 text-lg text-primary-foreground/80 leading-relaxed">
              Vores tilgang er data-drevet og fokuseret på ROI. Vi investerer kun 
              i de forbedringer, der giver dig det bedste afkast ved salg.
            </p>

            {/* Features Grid */}
            <div className="mt-10 grid grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-10 h-10 bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">{feature.title}</h4>
                    <p className="text-xs text-primary-foreground/70 mt-1">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_boligrenovering/artifacts/k3ixigj4_Sk%C3%A6rmbillede%202025-06-10%20kl.%2021.59.33.png"
                alt="Professionelt team"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Floating Stats Card */}
            <div className="absolute -bottom-8 -left-8 bg-white text-foreground p-6 shadow-xl">
              <div className="text-lg font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Skal dit boligsalg
              </div>
              <div className="text-lg font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>
                være en succes?
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
